import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "./useAuth";
import { ApiError } from "../lib/api";

function depsEqual(
	a: readonly unknown[],
	b: readonly unknown[],
): boolean {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
}

export function useApi<T>(
	fetcher: (token: string) => Promise<T>,
	deps: readonly unknown[] = [],
) {
	const { getToken, isSignedIn } = useAuth();
	const [data, setData] = useState<T | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [reloadKey, setReloadKey] = useState(0);
	const [depsVersion, setDepsVersion] = useState(0);

	const fetcherRef = useRef(fetcher);
	useEffect(() => {
		fetcherRef.current = fetcher;
	});

	const lastDepsRef = useRef<readonly unknown[]>(deps);
	useEffect(() => {
		if (!depsEqual(lastDepsRef.current, deps)) {
			lastDepsRef.current = deps;
			setDepsVersion((v) => v + 1);
		}
	}, [deps]);

	if (!isSignedIn) {
		if (data !== null) setData(null);
		if (error !== null) setError(null);
		if (loading) setLoading(false);
	}

	const reload = useCallback(() => setReloadKey((k) => k + 1), []);

	useEffect(() => {
		if (!isSignedIn) return;
		let cancelled = false;
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setLoading(true);
		setError(null);
		(async () => {
			try {
				const token = await getToken();
				if (!token) throw new ApiError("No auth token", 401);
				const result = await fetcherRef.current(token);
				if (!cancelled) setData(result);
			} catch (e) {
				if (!cancelled) setError(e instanceof Error ? e.message : String(e));
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isSignedIn, getToken, reloadKey, depsVersion]);

	return { data, error, loading, reload };
}
